import { ERROR_CODES, PROTOCOL_CONSTANTS } from "@klass-share/protocol";

import type { HostDiscovery } from "./discovery.js";
import { requireValidDisplayName } from "./display-name.js";
import { strings } from "./strings.js";
import {
  createConnId,
  type ConnIdFactory,
  type IdentityStore,
  type IdentitySnapshot,
} from "./identity.js";
import { applyRoomEvent, roomEventFromWire } from "./room-state.js";
import type {
  Capabilities,
  Envelope,
  JoinFailure,
  JoinPayload,
  JoinSuccess,
  LegCreatePayload,
  RoomState,
  RtcSignalPayload,
} from "./wire.js";
import {
  HEARTBEAT_FALLBACK_MS,
  HIDDEN_PONG_TOLERANCE_MS,
  isRecord,
  parseServerMessage,
  payloadRecord,
  PROTOCOL_VERSION,
  RECONNECT_BASE_MS,
  RECONNECT_CAP_MS,
  RECONNECT_ESCALATION_MS,
} from "./wire.js";

export type ConnectionPhase =
  | "idle"
  | "connecting"
  | "lna-prompt-pending"
  | "joined"
  | "reconnecting"
  | "reconnect-escalated"
  | "lna-denied"
  | "terminal-error"
  | "manual-reconnect"
  | "room-closed"
  | "stopped";

export interface SignalingSnapshot {
  phase: ConnectionPhase;
  clientId: string;
  capabilities: Capabilities;
  connId?: string;
  room?: RoomState;
  resumed?: boolean;
  transportMode?: "direct" | "relay" | "unreachable";
  lastError?: { code: string; message: string; fatal: boolean };
  yjs?: { ticket: string; sessionId: string };
  consecutiveFailureMs: number;
}

export type SignalingEvent =
  | { type: "state"; snapshot: SignalingSnapshot }
  | { type: "leg-create"; payload: LegCreatePayload }
  | { type: "leg-close"; legId: string }
  | { type: "rtc-signal"; payload: RtcSignalPayload }
  | { type: "share-decision"; payload: Record<string, unknown> }
  | { type: "share-revoke"; reason: string }
  | { type: "protocol-error"; code: string; message: string; fatal: boolean }
  | { type: "foreign-supersede" }
  | { type: "joined"; payload: JoinSuccess };

export function projectRetainedSignalingEvent(
  type: string,
  payload: Record<string, unknown>,
): SignalingEvent | undefined {
  switch (type) {
    case "leg-create":
      return isLegCreate(payload) ? { type, payload } : undefined;
    case "leg-close":
      return typeof payload.legId === "string"
        ? { type, legId: payload.legId }
        : undefined;
    case "rtc-signal":
      return isRtcSignal(payload) ? { type, payload } : undefined;
    case "share-decision":
      return { type, payload };
    case "share-revoke":
      return {
        type,
        reason: typeof payload.reason === "string" ? payload.reason : "revoked",
      };
    default:
      return undefined;
  }
}

export interface SignalingClientOptions {
  discovery: HostDiscovery;
  identityStore: IdentityStore;
  displayName: string;
  capabilities: Capabilities;
  appVersion?: string;
  webSocketFactory?: (url: string) => WebSocket;
  connIdFactory?: ConnIdFactory;
  random?: () => number;
  now?: () => number;
  document?: Pick<
    Document,
    "hidden" | "addEventListener" | "removeEventListener"
  >;
}

type Listener = (event: SignalingEvent) => void;

export class SignalingClient {
  readonly #discovery: HostDiscovery;
  readonly #identityStore: IdentityStore;
  readonly #webSocketFactory: (url: string) => WebSocket;
  readonly #connIdFactory: ConnIdFactory;
  readonly #random: () => number;
  readonly #now: () => number;
  readonly #document: SignalingClientOptions["document"];
  readonly #listeners = new Set<Listener>();
  readonly #pageConnIds = new Set<string>();

  #identity: IdentitySnapshot;
  #displayName: string;
  #capabilities: Capabilities;
  #appVersion: string | undefined;
  #socket: WebSocket | undefined;
  #connId: string | undefined;
  #sequence = 0;
  #room: RoomState | undefined;
  #phase: ConnectionPhase = "idle";
  #resumed: boolean | undefined;
  #transportMode: "direct" | "relay" | "unreachable" | undefined;
  #lastError: SignalingSnapshot["lastError"];
  #yjs: SignalingSnapshot["yjs"];
  #heartbeatIntervalMs: number = HEARTBEAT_FALLBACK_MS;
  #heartbeatTimeoutMs: number = PROTOCOL_CONSTANTS.T_HB_TIMEOUT;
  #pingTimer: ReturnType<typeof setTimeout> | undefined;
  #livenessTimer: ReturnType<typeof setTimeout> | undefined;
  #lnaTimer: ReturnType<typeof setTimeout> | undefined;
  #lnaEscalationTimer: ReturnType<typeof setTimeout> | undefined;
  #reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  #attempt = 0;
  #failureStartedAt: number | undefined;
  #stopped = false;
  #joinedOnce = false;
  #visibilityListenerRegistered = false;

  public constructor(options: SignalingClientOptions) {
    this.#discovery = options.discovery;
    this.#identityStore = options.identityStore;
    this.#identity = options.identityStore.load(options.discovery.hostId);
    this.#displayName = requireValidDisplayName(options.displayName);
    this.#capabilities = { ...options.capabilities };
    this.#appVersion = options.appVersion;
    this.#webSocketFactory =
      options.webSocketFactory ?? ((url) => new WebSocket(url));
    this.#connIdFactory = options.connIdFactory ?? createConnId;
    this.#random = options.random ?? Math.random;
    this.#now = options.now ?? (() => performance.now());
    this.#document = options.document ?? document;
  }

  public get snapshot(): SignalingSnapshot {
    const failureMs =
      this.#failureStartedAt === undefined
        ? 0
        : Math.max(0, this.#now() - this.#failureStartedAt);
    return {
      phase: this.#phase,
      clientId: this.#identity.clientId,
      capabilities: { ...this.#capabilities },
      ...(this.#connId === undefined ? {} : { connId: this.#connId }),
      ...(this.#room === undefined ? {} : { room: this.#room }),
      ...(this.#resumed === undefined ? {} : { resumed: this.#resumed }),
      ...(this.#transportMode === undefined
        ? {}
        : { transportMode: this.#transportMode }),
      ...(this.#lastError === undefined ? {} : { lastError: this.#lastError }),
      ...(this.#yjs === undefined ? {} : { yjs: this.#yjs }),
      consecutiveFailureMs: failureMs,
    };
  }

  public subscribe(listener: Listener): () => void {
    this.#listeners.add(listener);
    listener({ type: "state", snapshot: this.snapshot });
    return () => this.#listeners.delete(listener);
  }

  public connect(): void {
    if (this.#stopped || this.#socket !== undefined) return;
    this.#identity = this.#identityStore.load(this.#discovery.hostId);
    this.#openSocket();
  }

  public stop(sendLeave = false): void {
    this.#stopped = true;
    this.#clearTimers();
    this.#removeVisibilityListener();
    if (sendLeave && this.#socket?.readyState === 1) this.send("leave", {});
    this.#socket?.close(1000, "client stopped");
    this.#socket = undefined;
    this.#setPhase("stopped");
  }

  public retryAfterLnaRecovery(): void {
    if (this.#phase !== "lna-denied") return;
    this.#attempt = 0;
    this.#failureStartedAt = undefined;
    this.#lastError = undefined;
    this.#phase = "connecting";
    this.#openSocket();
  }

  public prepareFreshJoinAfterSupersede(): boolean {
    if (this.#phase !== "manual-reconnect") return false;
    return this.#prepareFreshJoinAfterSupersede();
  }

  /** Recovery entry for a foreign supersede observed on the external primary carrier. */
  public prepareFreshJoinAfterExternalPrimarySupersede(): boolean {
    if (this.#socket !== undefined) return false;
    return this.#prepareFreshJoinAfterSupersede();
  }

  #prepareFreshJoinAfterSupersede(): boolean {
    this.#identity = this.#identityStore.replaceContestedIdentity(
      this.#discovery.hostId,
    );
    this.#room = undefined;
    this.#resumed = undefined;
    this.#transportMode = undefined;
    this.#yjs = undefined;
    this.#lastError = undefined;
    this.#stopped = false;
    this.#attempt = 0;
    this.#failureStartedAt = undefined;
    this.#openSocket();
    return true;
  }

  public updateProfile(displayName: string, capabilities: Capabilities): void {
    this.#displayName = requireValidDisplayName(displayName);
    this.#capabilities = { ...capabilities };
    this.send("peer-update", {
      displayName: this.#displayName,
      capabilities: this.#capabilities,
    });
    if (this.#room !== undefined) {
      const self = this.#room.peers.find(
        (peer) => peer.clientId === this.#identity.clientId,
      );
      if (self !== undefined) {
        this.#room = {
          ...this.#room,
          peers: this.#room.peers.map((peer) =>
            peer.clientId === self.clientId
              ? {
                  ...peer,
                  displayName: this.#displayName,
                  capabilities: { ...capabilities },
                }
              : peer,
          ),
        };
        this.#emitState();
      }
    }
  }

  public markScreenCaptureUnavailable(
    sendUpdate = true,
    emitState = true,
  ): Capabilities | undefined {
    if (!this.#capabilities.canCaptureScreen) return undefined;
    this.#capabilities = { ...this.#capabilities, canCaptureScreen: false };
    if (sendUpdate)
      this.send("peer-update", { capabilities: this.#capabilities });
    if (this.#room !== undefined)
      this.#room = {
        ...this.#room,
        peers: this.#room.peers.map((peer) =>
          peer.clientId === this.#identity.clientId
            ? { ...peer, capabilities: { ...this.#capabilities } }
            : peer,
        ),
      };
    if (emitState) this.#emitState();
    return { ...this.#capabilities };
  }

  public send(type: string, payload: Record<string, unknown>): boolean {
    if (this.#socket?.readyState !== 1) return false;
    const envelope: Envelope = {
      v: PROTOCOL_VERSION,
      type,
      seq: ++this.#sequence,
      payload,
    };
    this.#socket.send(JSON.stringify(envelope));
    return true;
  }

  public requestShare(): boolean {
    return this.send("share-request", {});
  }

  public sendShareReady(grantId: string): boolean {
    return this.send("share-ready", { grantId });
  }

  public stopShare(reason: "cancelled" | "capture-failed"): boolean {
    return this.send("share-stop", { reason });
  }

  public sendRtcSignal(payload: Omit<RtcSignalPayload, "from">): boolean {
    const safePayload = Object.fromEntries(
      Object.entries(payload).filter(([field]) => field !== "from"),
    );
    return this.send("rtc-signal", safePayload);
  }

  #openSocket(): void {
    if (this.#stopped || this.#phase === "lna-denied") return;
    this.#ensureVisibilityListener();
    this.#clearSocketTimers();
    this.#connId = this.#connIdFactory();
    this.#pageConnIds.add(this.#connId);
    this.#sequence = 0;
    const failureEscalated =
      this.#failureStartedAt !== undefined &&
      this.#now() - this.#failureStartedAt >= RECONNECT_ESCALATION_MS;
    this.#phase = failureEscalated
      ? "reconnect-escalated"
      : this.#joinedOnce
        ? "reconnecting"
        : "connecting";
    this.#emitState();

    const socket = this.#webSocketFactory(this.#discovery.signalUrl);
    this.#socket = socket;
    this.#lnaTimer = setTimeout(() => {
      if (
        this.#socket === socket &&
        socket.readyState === 0 &&
        !this.#joinedOnce
      ) {
        this.#setPhase("lna-prompt-pending");
      }
    }, 1_500);
    if (!this.#joinedOnce) {
      if (this.#failureStartedAt === undefined)
        this.#failureStartedAt = this.#now();
      this.#lnaEscalationTimer = setTimeout(() => {
        if (this.#socket !== socket || this.#joinedOnce) return;
        if (socket.readyState === 0) {
          this.#setPhase("lna-denied");
          socket.close(4000, "local network access unavailable");
          return;
        }
        this.#setPhase("reconnect-escalated");
        socket.close(4000, "join did not complete");
      }, RECONNECT_ESCALATION_MS);
    }

    socket.addEventListener("open", () => {
      if (this.#socket !== socket) return;
      this.#clearLnaTimer();
      this.#sendJoin(this.#identity.resume !== undefined);
    });
    socket.addEventListener("message", (event) => {
      if (this.#socket !== socket || typeof event.data !== "string") return;
      this.#onMessage(event.data);
    });
    socket.addEventListener("close", () => {
      if (this.#socket !== socket) return;
      this.#socket = undefined;
      this.#clearSocketTimers();
      if (!this.#stopped && !isReconnectTerminalPhase(this.#phase)) {
        this.#scheduleReconnect();
      }
    });
    socket.addEventListener("error", () => {
      // Browser WebSocket errors deliberately expose no network detail. `close`
      // drives the retry/LNA state machine without inventing a cause.
    });
  }

  #sendJoin(resume: boolean): void {
    if (this.#connId === undefined) return;
    const resumeToken = resume ? this.#identity.resume?.resumeToken : undefined;
    const base = {
      clientId: this.#identity.clientId,
      connId: this.#connId,
      displayName: this.#displayName,
      capabilities: this.#capabilities,
      proto: { min: PROTOCOL_VERSION, max: PROTOCOL_VERSION },
      ...(this.#appVersion === undefined
        ? {}
        : { appVersion: this.#appVersion }),
    };
    const payload: JoinPayload =
      resumeToken === undefined
        ? {
            ...base,
            resume: false,
          }
        : { ...base, resume: true, resumeToken };
    this.send("join", payload as unknown as Record<string, unknown>);
  }

  #onMessage(raw: string): void {
    const message = parseServerMessage(raw);
    if (message === undefined) return;
    this.#armLivenessTimer();
    const payload = payloadRecord(message);

    if (message.type === "pong") {
      this.#schedulePingFromPong();
      return;
    }
    if (message.type === "join-ack") {
      this.#onJoinAck(payload);
      return;
    }
    const roomEvent = roomEventFromWire(message.type, payload);
    if (roomEvent !== undefined && this.#room !== undefined) {
      this.#room = applyRoomEvent(this.#room, roomEvent);
      this.#emitState();
    }
    const projected = projectRetainedSignalingEvent(message.type, payload);
    if (projected !== undefined) this.#emit(projected);
    switch (message.type) {
      case "transport-changed":
        if (
          payload.mode === "direct" ||
          payload.mode === "relay" ||
          payload.mode === "unreachable"
        ) {
          this.#transportMode = payload.mode;
          this.#emitState();
        }
        break;
      case "error":
        this.#onProtocolError(payload);
        break;
      case "kick":
        this.#onKick(payload);
        break;
    }
  }

  #onJoinAck(payload: Record<string, unknown>): void {
    if (payload.ok === false) {
      this.#onJoinFailure(payload as unknown as JoinFailure);
      return;
    }
    if (!isJoinSuccess(payload)) return;
    this.#identityStore.saveResume(this.#discovery.hostId, {
      resumeToken: payload.resumeToken,
      sessionId: payload.sessionId,
    });
    this.#identity = {
      clientId: this.#identity.clientId,
      resume: {
        resumeToken: payload.resumeToken,
        sessionId: payload.sessionId,
      },
    };
    this.#room = applyRoomEvent(undefined, {
      type: "snapshot",
      roomState: payload.roomState,
    });
    this.#resumed = payload.resumed;
    this.#yjs = { ticket: payload.yjsTicket, sessionId: payload.sessionId };
    this.#heartbeatIntervalMs = payload.heartbeat.intervalMs;
    this.#heartbeatTimeoutMs = payload.heartbeat.timeoutMs;
    this.#joinedOnce = true;
    this.#clearLnaEscalationTimer();
    this.#attempt = 0;
    this.#failureStartedAt = undefined;
    this.#lastError = undefined;
    this.#setPhase("joined");
    this.send("sync-request", {});
    this.send("ping", {});
    this.#emit({ type: "joined", payload });
  }

  #onJoinFailure(payload: JoinFailure): void {
    const fatal = isFatalJoinCode(payload.code);
    this.#handleAuthOrError(payload.code, payload.message, fatal, "join-ack");
  }

  #onProtocolError(payload: Record<string, unknown>): void {
    const code = typeof payload.code === "string" ? payload.code : "INTERNAL";
    const message =
      typeof payload.message === "string" ? payload.message : "Protocol error";
    const fatal = payload.fatal === true;
    this.#handleAuthOrError(code, message, fatal, "protocol-error");
  }

  #handleAuthOrError(
    code: string,
    message: string,
    fatal: boolean,
    source: "join-ack" | "protocol-error",
  ): void {
    this.#lastError = { code, message, fatal };
    if (code === "RESUME_UNAUTHORIZED") {
      this.#identity = this.#identityStore.replaceContestedIdentity(
        this.#discovery.hostId,
      );
      this.#room = undefined;
      this.#resumed = undefined;
      this.#transportMode = undefined;
      this.#yjs = undefined;
      this.#lastError = undefined;
      this.#sendJoin(false);
    } else if (code === "ROOM_CLOSED" && fatal) {
      this.#markTerminal();
      this.#setPhase("room-closed");
    } else if (
      code === ERROR_CODES.RATE_LIMITED.code &&
      !fatal &&
      source === "join-ack"
    ) {
      // The host deliberately keeps RATE_LIMITED retryable. Rotate the socket
      // through the existing bounded reconnect ladder so the client cannot
      // remain indefinitely parked on a pre-join connection.
      this.#emitState();
      this.#socket?.close(1000, "join rate limited");
    } else if (fatal) {
      this.#markTerminal();
      this.#setPhase("terminal-error");
    } else {
      this.#emitState();
    }
    this.#emit({ type: "protocol-error", code, message, fatal });
  }

  #onKick(payload: Record<string, unknown>): void {
    const reason = payload.reason;
    if (reason === "superseded") {
      const supersededBy = payload.supersededBy;
      if (
        typeof supersededBy === "string" &&
        this.#pageConnIds.has(supersededBy)
      )
        return;
      this.#halt("manual-reconnect", "foreign supersede");
      this.#emit({ type: "foreign-supersede" });
    } else if (reason === "room-closed") {
      this.#lastError = {
        code: ERROR_CODES.ROOM_CLOSED.code,
        message: strings.errors.signaling.roomClosed,
        fatal: true,
      };
      this.#halt("room-closed", "room closed");
    } else if (reason === "admin") {
      this.#halt("stopped", "removed by administrator");
    }
  }

  #markTerminal(): void {
    this.#stopped = true;
    this.#clearTimers();
    this.#removeVisibilityListener();
  }

  #halt(phase: ConnectionPhase, reason: string): void {
    this.#markTerminal();
    const socket = this.#socket;
    this.#socket = undefined;
    socket?.close(1000, reason);
    this.#setPhase(phase);
  }

  #schedulePingFromPong(): void {
    if (this.#pingTimer !== undefined) clearTimeout(this.#pingTimer);
    this.#pingTimer = setTimeout(() => {
      this.#pingTimer = undefined;
      this.send("ping", {});
    }, this.#heartbeatIntervalMs);
  }

  #armLivenessTimer(): void {
    if (this.#livenessTimer !== undefined) clearTimeout(this.#livenessTimer);
    const timeout = this.#document?.hidden
      ? Math.max(this.#heartbeatTimeoutMs, HIDDEN_PONG_TOLERANCE_MS)
      : this.#heartbeatTimeoutMs;
    this.#livenessTimer = setTimeout(() => {
      this.#livenessTimer = undefined;
      this.#socket?.close(4000, "host liveness timeout");
    }, timeout);
  }

  readonly #onVisibilityChange = (): void => {
    this.#armLivenessTimer();
    if (this.#document?.hidden === false) {
      this.send("ping", {});
      this.send("sync-request", {});
    }
  };

  #ensureVisibilityListener(): void {
    if (this.#document === undefined || this.#visibilityListenerRegistered)
      return;
    this.#document.addEventListener(
      "visibilitychange",
      this.#onVisibilityChange,
    );
    this.#visibilityListenerRegistered = true;
  }

  #removeVisibilityListener(): void {
    if (this.#document === undefined || !this.#visibilityListenerRegistered)
      return;
    this.#document.removeEventListener(
      "visibilitychange",
      this.#onVisibilityChange,
    );
    this.#visibilityListenerRegistered = false;
  }

  #scheduleReconnect(): void {
    if (this.#failureStartedAt === undefined)
      this.#failureStartedAt = this.#now();
    const failedFor = this.#now() - this.#failureStartedAt;
    if (failedFor >= RECONNECT_ESCALATION_MS) {
      this.#setPhase("reconnect-escalated");
    } else {
      this.#setPhase("reconnecting");
    }
    const ceiling = Math.min(
      RECONNECT_CAP_MS,
      RECONNECT_BASE_MS * 2 ** this.#attempt,
    );
    const delay = Math.floor(this.#random() * ceiling);
    this.#attempt += 1;
    this.#reconnectTimer = setTimeout(() => {
      this.#reconnectTimer = undefined;
      this.#openSocket();
    }, delay);
  }

  #setPhase(phase: ConnectionPhase): void {
    this.#phase = phase;
    this.#emitState();
  }

  #emitState(): void {
    this.#emit({ type: "state", snapshot: this.snapshot });
  }

  #emit(event: SignalingEvent): void {
    for (const listener of this.#listeners) listener(event);
  }

  #clearLnaTimer(): void {
    if (this.#lnaTimer !== undefined) clearTimeout(this.#lnaTimer);
    this.#lnaTimer = undefined;
  }

  #clearLnaEscalationTimer(): void {
    if (this.#lnaEscalationTimer !== undefined)
      clearTimeout(this.#lnaEscalationTimer);
    this.#lnaEscalationTimer = undefined;
  }

  #clearSocketTimers(): void {
    this.#clearLnaTimer();
    this.#clearLnaEscalationTimer();
    if (this.#pingTimer !== undefined) clearTimeout(this.#pingTimer);
    if (this.#livenessTimer !== undefined) clearTimeout(this.#livenessTimer);
    this.#pingTimer = undefined;
    this.#livenessTimer = undefined;
  }

  #clearTimers(): void {
    this.#clearSocketTimers();
    if (this.#reconnectTimer !== undefined) clearTimeout(this.#reconnectTimer);
    this.#reconnectTimer = undefined;
  }
}

function isJoinSuccess(
  payload: Record<string, unknown>,
): payload is unknown & JoinSuccess {
  return (
    payload.ok === true &&
    typeof payload.resumed === "boolean" &&
    typeof payload.resumeToken === "string" &&
    typeof payload.yjsTicket === "string" &&
    typeof payload.proto === "number" &&
    typeof payload.hostVersion === "string" &&
    typeof payload.sessionId === "string" &&
    isRecord(payload.roomState) &&
    isRecord(payload.heartbeat) &&
    typeof payload.heartbeat.intervalMs === "number" &&
    typeof payload.heartbeat.timeoutMs === "number"
  );
}

function isFatalJoinCode(code: string): boolean {
  if (code === ERROR_CODES.INVALID_PAYLOAD.code) return true;
  return Object.values(ERROR_CODES).some(
    (definition) => definition.code === code && definition.fatal,
  );
}

function isReconnectTerminalPhase(phase: ConnectionPhase): boolean {
  return (
    phase === "lna-denied" ||
    phase === "terminal-error" ||
    phase === "manual-reconnect" ||
    phase === "room-closed" ||
    phase === "stopped"
  );
}

function isLegCreate(
  payload: Record<string, unknown>,
): payload is unknown & LegCreatePayload {
  return (
    typeof payload.legId === "string" &&
    typeof payload.epoch === "number" &&
    (payload.role === "offerer" || payload.role === "answerer") &&
    typeof payload.peer === "string" &&
    typeof payload.h264Only === "boolean"
  );
}

function isRtcSignal(
  payload: Record<string, unknown>,
): payload is unknown & RtcSignalPayload {
  return (
    typeof payload.legId === "string" &&
    typeof payload.to === "string" &&
    typeof payload.from === "string" &&
    (payload.kind === "offer" ||
      payload.kind === "answer" ||
      payload.kind === "ice" ||
      payload.kind === "ice-restart-offer") &&
    isRecord(payload.data)
  );
}
